import { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { fontInfo, hasBold, hasThai, THAI_FALLBACK } from '../fonts/catalog';
import { fontNeeds, loadFonts } from '../fonts/load';
import { fitInto } from '../editor/fit';
import { isThaiText } from '../thai/ThaiTextbox';
import { isEdited } from '../editor/photo';
import { arrowEndsOf, dashArray, dashStyleOf, PENS, withAlpha, type ArrowEnds, type DashStyle } from '../editor/drawing';
import { $, $in, setPressed } from './dom';
import { initFontSheet } from './fontSheet';
import { setLocked } from './layersSheet';
import { quarterOf } from '../editor/crop';
import { initBgRemove } from './bgRemove';
import { initCropView } from './cropView';
import { initFixView } from './fixView';
import { initPhotoSheet } from './photoSheet';
import { colourSlots, type Slot } from '../graphics/graphics';
import { cleanFx, FX_HAS_COLOR, FX_KINDS, FX_PARAMS, newFx, paletteLook, PALETTES, type FxKind, type TextFx } from '../editor/textFx';
import { setTextFx, setTextInk, textInk } from '../editor/textFxDraw';
import { kindName } from './objects';
import { isTable } from '../table/tableFabric';
import { slotOf } from '../editor/picSlot';
import { isOpen } from './sheets';
import { toast } from './toast';


const ICON = {
  align: '<svg viewBox="0 0 24 24"><path d="M4 6h16M4 12h10M4 18h13"/></svg>',
  spacing: '<svg viewBox="0 0 24 24"><path d="M8 5v14M16 5v14M3 12h2M19 12h2"/></svg>',
  opacity: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="8"/><path d="M12 4a8 8 0 010 16z" fill="currentColor"/></svg>',
  stroke: '<svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>',
  radius: '<svg viewBox="0 0 24 24"><path d="M4 20V11a7 7 0 017-7h9"/></svg>',
  thickness: '<svg viewBox="0 0 24 24"><path d="M4 7h16" stroke-width="1.5"/><path d="M4 12h16" stroke-width="3"/><path d="M4 18h16" stroke-width="5"/></svg>',
  cover: '<svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="1"/><path d="M3 9h18M3 15h18" opacity=".45"/></svg>',
  contain: '<svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="1"/><rect x="7" y="8" width="10" height="8"/></svg>',
  background: '<svg viewBox="0 0 24 24"><rect x="3" y="7" width="14" height="12" rx="1"/><path d="M7 7V5h14v12h-4"/></svg>',
  photo: '<svg viewBox="0 0 24 24"><path d="M4 20l6-6 3 3 3-4 4 7z"/><circle cx="8" cy="8" r="2"/><path d="M16 3v4M14 5h4"/></svg>',
  ai: '<svg viewBox="0 0 24 24"><path d="M10 3l1.8 4.7L16.5 9.5l-4.7 1.8L10 16l-1.8-4.7L3.5 9.5l4.7-1.8z"/><path d="M18 14l.9 2.1L21 17l-2.1.9L18 20l-.9-2.1L15 17l2.1-.9z"/></svg>',
  crop: '<svg viewBox="0 0 24 24"><path d="M6 2v16h16M2 6h16v16"/></svg>',
  lineStyle: '<svg viewBox="0 0 24 24"><path d="M3 7h18" /><path d="M3 12h4M10 12h4M17 12h4"/><path d="M3 17h12M15 14l4 3-4 3"/></svg>',
  fx: '<svg viewBox="0 0 24 24"><path d="M5 18h14"/><path d="M8 15l4-10 4 10M9.5 11.5h5"/><path d="M18 4l1 2 2 1-2 1-1 2-1-2-2-1 2-1z"/></svg>',
  shuffle: '<svg viewBox="0 0 24 24"><path d="M4 7h11l-3-3M20 17H9l3 3"/><circle cx="18" cy="7" r="2"/><circle cx="6" cy="17" r="2"/></svg>',
  peek: '<svg viewBox="0 0 24 24"><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>',
  doc: '<svg viewBox="0 0 24 24"><path d="M6 3h9l4 4v14H6z"/><path d="M14 3v5h5M9 12h7M9 16h5"/></svg>',
  table: '<svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9h18M3 14h18M9 4v16M15 4v16"/></svg>',
  inText: '<svg viewBox="0 0 24 24"><path d="M12 3l2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5z" opacity=".35"/><path d="M9 9h6M12 9v7"/></svg>'
};

/**
 * A chip in the contextual toolbar. `tool` opens rows tagged with it in the tool panel; `act` runs at once.
 * `off` = why it does nothing for this object (shown greyed; a tap says why).
 */
interface Chip { tool?: string; act?: () => void; label: string; html: string; pressed?: boolean; off?: string;
  /** works while held (ดูต้นฉบับ), not on a tap */
  hold?: (el: HTMLElement) => void }

const toHex = (c: unknown) => {
  if (typeof c !== 'string' || !c) return '#000000';
  if (/^#[0-9a-f]{6}$/i.test(c)) return c;
  return '#' + new fabric.Color(c).toHex();
};
const isBold = (w: unknown) => w === 'bold' || Number(w) >= 600;
/** a stroke drawn with ปากกา/มาร์กเกอร์/ไฮไลต์ */
const isPen = (o: fabric.Object) => o.type === 'path' && !!(o as { pen?: string }).pen;
const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
const dot = (c: unknown) => `<span class="dot" style="background:${esc(toHex(c))}"></span>`;

/** `canAiImage`: may this account make AI pictures (รูป AI, 0022)? — the chip shows only then (the server checks too) */
export function initToolbar(ed: Editor, history: History, onChanged: () => void, canEdit: () => boolean = () => true, canAiImage: () => boolean = () => false) {
  const { canvas } = ed;
  let openTool: string | null = null;

  const single = () => { const o = canvas.getActiveObject(); return o && o.type !== 'activeSelection' ? o : null; };

  /** loads a font weight before text is measured with it; false (and nothing changes) when it cannot be loaded */
  async function ensureFont(family: string, weight: unknown): Promise<boolean> {
    const failed = await loadFonts(fontNeeds([{ type: 'thaiTextbox', fontFamily: family, fontWeight: weight }]));
    fabric.util.clearFabricFontCache(family);
    if (failed.length) { toast(`โหลดฟอนต์ ${family} ไม่สำเร็จ ตรวจอินเทอร์เน็ตแล้วลองใหม่ ยังไม่ได้เปลี่ยน`, true); return false; }
    return true;
  }

  function apply(fn: (o: fabric.Object) => void) {
    const o = single(); if (!o) return;
    fn(o);
    if (isThaiText(o)) o.initDimensions();
    o.setCoords(); canvas.requestRenderAll();
    sync(); history.push(); onChanged();
  }

  async function toggleBold() {
    const o = single(); if (!isThaiText(o)) return;
    const bold = !isBold(o.fontWeight);
    if (!await ensureFont(o.fontFamily ?? 'Sarabun', bold ? 700 : 400)) return;
    apply(x => x.set('fontWeight' as keyof fabric.Object, bold ? 700 : 400));
  }

  /* ---------- แต่งภาพ (src/ui/photoSheet.ts) ---------- */
  const photoSheet = initPhotoSheet(ed, history, () => sync());
  const cropView = initCropView(ed, history);
  // ลบพื้นหลัง on the group's VM (src/ui/bgRemove.ts); the chips are drawn again when it starts / ends
  const bgRemove = initBgRemove(ed, history, () => sync());
  // แก้รูป: ลบด้วย AI + แปรงทาสี + ประทับตรา (src/ui/fixView.ts); "คืนต้นฉบับ" once edited
  const fixView = initFixView(ed, history, { canEdit, refresh: () => sync() });

  /* ---------- font sheet (src/ui/fontSheet.ts) ---------- */
  const fontSheet = initFontSheet({
    current: () => { const o = single(); return isThaiText(o) ? { family: o.fontFamily ?? 'Sarabun', text: String(o.text ?? '') } : null; },
    async pick(family) {
      const o = single(); if (!isThaiText(o)) return 'ไม่ได้เลือกข้อความไว้';
      // a font without a real bold: the text becomes regular, so the screen and the print PDF look the same
      const dropBold = isBold(o.fontWeight) && fontInfo(family) && !hasBold(family);
      const weight = dropBold ? 400 : o.fontWeight;
      const failed = await loadFonts(fontNeeds([{ type: 'thaiTextbox', fontFamily: family, fontWeight: weight }]));
      if (failed.length) return `โหลดฟอนต์ ${family} ไม่สำเร็จ ตรวจอินเทอร์เน็ตแล้วลองใหม่ (ยังไม่ได้เปลี่ยนฟอนต์)`;
      fabric.util.clearFabricFontCache(family);
      if (single() !== o) return 'ข้อความที่เลือกเปลี่ยนไประหว่างโหลดฟอนต์ ลองเลือกใหม่';
      apply(x => { x.set('fontFamily' as keyof fabric.Object, family); if (dropBold) x.set('fontWeight' as keyof fabric.Object, 400); });
      const notes: string[] = [];
      if (dropBold) notes.push(`${family} ไม่มีตัวหนา จึงเปลี่ยนเป็นตัวปกติ`);
      if (fontInfo(family)?.thai === false && hasThai(o.text)) notes.push(`${family} ไม่มีตัวไทย ตัวไทยใช้ ${THAI_FALLBACK} แทน (PDF โรงพิมพ์ กล่องนี้จะเป็นภาพ 300 DPI)`);
      if (notes.length) toast(notes.join(' · '));
      return null;
    }
  });

  function chipsFor(o: fabric.Object): Chip[] {
    const opacity: Chip = { tool: 'opacity', label: 'ความทึบ', html: `${ICON.opacity}<span>ความทึบ</span>` };
    // เอกสาร: a ช่องข้อมูล is filled in the document panel (src/ui/docFill.ts) — its chip first
    const fill: Chip[] = (o as { field?: unknown }).field ? [{ act: () => ed.canvas.fire('doc:fill' as never), label: 'กรอกข้อมูลเอกสาร', html: `${ICON.doc}<span>กรอกข้อมูล</span>` }] : [];
    if (isThaiText(o)) return [
      ...fill,
      { act: () => fontSheet.open(), label: 'ฟอนต์', html: `<b style="font-family:'${esc(o.fontFamily ?? '')}'">${esc(o.fontFamily ?? '')}</b><span>ฟอนต์</span>` },
      { tool: 'size', label: 'ขนาด', html: `<b>${Math.round(o.fontSize ?? 0)}</b><span>ขนาด</span>` },
      { tool: 'textColor', label: 'สีตัวอักษร', html: `${dot(textInk(o))}<span>สี</span>` },
      { act: () => shuffleColours(), label: 'สลับสี', html: `${ICON.shuffle}<span>สลับสี</span>` },
      { tool: 'textFx', label: 'เอฟเฟกต์', html: `${ICON.fx}<span>${esc(FX_KINDS.find(k => k.id === cleanFx((o as { textFx?: unknown }).textFx)?.kind)?.th ?? 'เอฟเฟกต์')}</span>`, pressed: false },
      {
        act: toggleBold, label: 'ตัวหนา', html: '<b>B</b><span>ตัวหนา</span>', pressed: isBold(o.fontWeight),
        off: fontInfo(o.fontFamily) && !hasBold(o.fontFamily) && !isBold(o.fontWeight)
          ? `ฟอนต์ ${o.fontFamily} ไม่มีตัวหนา (ถ้าทำหนาบนจอ PDF โรงพิมพ์จะไม่หนาตาม) เลือกฟอนต์อื่นถ้าต้องการตัวหนา` : undefined
      },
      { tool: 'align', label: 'จัดแนว', html: `${ICON.align}<span>จัดแนว</span>` },
      { tool: 'spacing', label: 'ระยะ', html: `${ICON.spacing}<span>ระยะ</span>` },
      opacity
    ];
    if (o.type === 'line') return [
      { tool: 'fill', label: 'สีเส้น', html: `${dot(o.stroke)}<span>สีเส้น</span>` },
      { tool: 'thickness', label: 'ความหนา', html: `${ICON.thickness}<span>ความหนา</span>` },
      // เส้นประ · ปลายมน · หัวลูกศร (src/editor/drawing.ts, src/editor/arrows.ts)
      { tool: 'lineStyle', label: 'รูปแบบเส้น', html: `${ICON.lineStyle}<span>รูปแบบเส้น</span>` },
      opacity
    ];
    // a stroke drawn with ปากกา/มาร์กเกอร์/ไฮไลต์ (src/ui/drawTools.ts)
    if (isPen(o)) return [
      { tool: 'fill', label: 'สีเส้น', html: `${dot(o.stroke)}<span>สีเส้น</span>` },
      { tool: 'thickness', label: 'ความหนา', html: `${ICON.thickness}<span>ความหนา</span>` },
      opacity
    ];
    // ช่องใส่รูปของเทมเพลต (src/editor/picSlot.ts): ใส่รูป · ความทึบ (src/ui/slotFill.ts)
    if (slotOf(o)) return [
      ...fill,
      { act: () => ed.canvas.fire('slot:fill' as never, { target: o } as never), label: 'ใส่รูปของคุณในช่องนี้', html: `${ICON.photo}<span>ใส่รูป</span>` },
      // รูป AI (0022, src/ui/aiImage.ts): for admins and members given the right
      ...(canAiImage() ? [{ act: () => ed.canvas.fire('slot:ai' as never, { target: o } as never), label: 'สร้างรูปด้วย AI ใส่ช่องนี้', html: `${ICON.ai}<span>สร้างด้วย AI</span>` }] : []),
      opacity
    ];
    // รูปทรงจากคลัง (src/editor/shapeLib.ts): สีพื้น · เส้นขอบ · พิมพ์ข้อความในรูปทรง (src/ui/shapePanel.ts)
    if (o.type === 'path' && (o as { shapeId?: string }).shapeId) return [
      { tool: 'fill', label: 'สีพื้น', html: `${dot(o.fill)}<span>สีพื้น</span>` },
      { tool: 'stroke', label: 'เส้นขอบ', html: `${ICON.stroke}<span>เส้นขอบ</span>` },
      { act: () => ed.canvas.fire('shape:text' as never, { target: o } as never), label: 'พิมพ์ข้อความในรูปทรง (หรือแตะรูปทรงสองครั้ง)', html: `${ICON.inText}<span>ข้อความ</span>` },
      opacity
    ];
    if (['rect', 'circle', 'triangle', 'polygon'].includes(o.type ?? '')) {
      const list: Chip[] = [
        { tool: 'fill', label: 'สีพื้น', html: `${dot(o.fill)}<span>สีพื้น</span>` },
        { tool: 'stroke', label: 'เส้นขอบ', html: `${ICON.stroke}<span>เส้นขอบ</span>` }
      ];
      if (o.type === 'rect') list.push({ tool: 'radius', label: 'มุมโค้ง', html: `${ICON.radius}<span>มุมโค้ง</span>` });
      list.push(opacity);
      return list;
    }
    // ตาราง (src/ui/tableView.ts): its cells are edited in the table sheet, not with the graphic's colour boxes
    if (isTable(o)) return [
      { act: () => ed.canvas.fire('table:edit' as never, { target: o } as never), label: 'แก้ตาราง (หรือแตะตารางสองครั้ง)', html: `${ICON.table}<span>แก้ตาราง</span>` },
      opacity
    ];
    // กราฟิก (0019): an SVG put in as shapes — one colour box per colour, as Canva does
    if (o.type === 'group') {
      const slots = gfxSlots(o);
      return [
        { tool: 'gfxColor', label: 'สีของกราฟิก', html: `${slots.slice(0, 4).map(s => dot(s.colour)).join('')}<span>สี</span>`,
          off: slots.length ? undefined : 'กราฟิกนี้ไม่มีสีที่เปลี่ยนได้ (สีไล่ระดับ หรือเป็นภาพ)' },
        opacity
      ];
    }
    if (o.type === 'image') {
      // a picture with a แต่งภาพ look: a dot on its chip, and ดูต้นฉบับ (held) next to it
      const edited = isEdited((o as { photoEdit?: unknown }).photoEdit);
      return [
      { act: () => void photoSheet.open(), label: edited ? 'แต่งภาพ (แต่งไว้แล้ว)' : 'แต่งภาพ', html: `${ICON.photo}<span>แต่งภาพ</span>${edited ? '<i class="edmark"></i>' : ''}` },
      ...(edited ? [{ hold: photoSheet.holdOriginal, label: 'ดูต้นฉบับ (กดค้าง)', html: `${ICON.peek}<span>ดูต้นฉบับ</span>` } as Chip] : []),
      { act: () => cropView.open(), label: 'ครอป', html: `${ICON.crop}<span>ครอป</span>` },
      bgRemove.chip(o as Parameters<typeof bgRemove.chip>[0]),
      ...fixView.chips(o as Parameters<typeof fixView.chips>[0]),
      { act: () => fitPicture('cover'), label: 'เต็มหน้า', html: `${ICON.cover}<span>เต็มหน้า</span>` },
      { act: () => fitPicture('contain'), label: 'พอดีหน้า', html: `${ICON.contain}<span>พอดีหน้า</span>` },
      {
        act: () => fitPicture('background'), label: 'เป็นพื้นหลัง', html: `${ICON.background}<span>เป็นพื้นหลัง</span>`,
        // over a video the picture would cover the clips for its whole time and be locked there
        off: document.querySelector('.app')?.classList.contains('vedit') ? 'บนวิดีโอใช้ "เป็นพื้นหลัง" ไม่ได้ (รูปจะบังคลิปทั้งช่วงเวลาของมัน) ใช้ "เต็มหน้า" แล้วตั้งเวลาขึ้นแทน' : undefined
      },
      opacity
    ];
    }
    return [opacity];
  }

  /**
   * A picture sized to the page, keeping its proportions:
   *   cover       fills the page out to the bleed line (choice ก); what goes past it is cut off when exported
   *   contain     all of the picture inside the page (trim edge), centred
   *   background  cover + bottom of the stack + locked (unlock in "เลเยอร์")
   * A turned picture is straightened first (it could not fill the page turned).
   */
  function fitPicture(mode: 'cover' | 'contain' | 'background') {
    const o = single(); if (!o || o.type !== 'image') return;
    // a quarter turn (ครอป "หมุน 90°") is kept; any other angle is set straight
    const a = o.angle ?? 0, q = Math.abs(a - Math.round(a / 90) * 90) < 0.01 ? quarterOf(a) : 0, side = q % 2 === 1;
    let fit: ReturnType<typeof fitInto>;
    try { fit = fitInto(side ? o.height ?? 0 : o.width ?? 0, side ? o.width ?? 0 : o.height ?? 0, ed.pageBox(mode !== 'contain'), mode === 'contain' ? 'contain' : 'cover'); }
    catch (e) { toast(e instanceof Error ? e.message : String(e), true); return; }
    const d = (((a - q * 90) % 360) + 360) % 360, turned = d > 0.01 && d < 359.99;   // not already q × 90°
    let below = 0;   // locked backgrounds left under the new one
    apply(x => {
      x.set({ angle: q * 90, scaleX: fit.scale, scaleY: fit.scale });
      x.setCoords();
      const b = x.getBoundingRect(true, true);
      x.set({ left: (x.left ?? 0) + fit.left - b.left, top: (x.top ?? 0) + fit.top - b.top });
      if (mode === 'background') {
        // just above the artboard and any background already locked at the bottom (it stays visible, not hidden under one)
        const all = canvas.getObjects();
        let at = all.indexOf(ed.board) + 1;
        while (at < all.length && all[at] !== x && (all[at] as { locked?: boolean }).locked) at++;
        below = at - all.indexOf(ed.board) - 1;
        canvas.moveTo(x, at);
        setLocked(x as Parameters<typeof setLocked>[0], true);
        canvas.discardActiveObject();
      }
    });
    toast((turned ? 'ตั้งรูปให้ตรงแล้ว · ' : '') + (
      mode === 'cover' ? `รูปเต็มหน้าแล้ว${ed.doc.bleedMm ? ' (ถึงเส้นตัดตก)' : ''} ส่วนที่เกินขอบจะถูกตัดตอนส่งออก`
      : mode === 'contain' ? 'รูปพอดีหน้าแล้ว เห็นครบทั้งรูป'
      : below ? `ตั้งเป็นพื้นหลังแล้ว ล็อกไว้ วางเหนือพื้นหลังเดิม ${below} ชิ้น (ลบหรือปลดล็อกของเดิมได้ที่ "เลเยอร์")`
      : 'ตั้งเป็นพื้นหลังแล้ว อยู่ล่างสุดและล็อกไว้ ปลดล็อกได้ที่ "เลเยอร์"'));
  }

  /*
   * The colours of the chosen graphic, worked out once when it is chosen: changing one colour to another one it already
   * has keeps two boxes (the boxes do not move under the finger), each still changing its own shapes.
   */
  let slotsOf: { o: fabric.Object; slots: Array<Slot<fabric.Object>> } | null = null;
  const MAX_SLOTS = 12;
  function gfxSlots(o: fabric.Object): Array<Slot<fabric.Object>> {
    if (slotsOf?.o === o) return slotsOf.slots;
    const leaves: fabric.Object[] = [];
    const walk = (x: fabric.Object) => { if (x.type === 'group') (x as fabric.Group).getObjects().forEach(walk); else leaves.push(x); };
    walk(o);
    const key = (c: string) => { try { const k = new fabric.Color(c); return k.getAlpha() > 0 ? '#' + k.toHex() : null; } catch { return null; } };
    slotsOf = { o, slots: colourSlots(leaves, key) };
    return slotsOf.slots;
  }
  /** one colour box per colour (rebuilt only when another graphic is chosen — never under the finger) */
  let colourBoxesFor: fabric.Object | null = null;
  function syncGfxColours(o: fabric.Object) {
    const box = $('pGfxColors'), slots = gfxSlots(o);
    if (colourBoxesFor !== o) {
      colourBoxesFor = o;
      box.textContent = '';
      slots.slice(0, MAX_SLOTS).forEach((s, i) => {
        const input = document.createElement('input');
        input.type = 'color'; input.value = s.colour;
        input.setAttribute('aria-label', `สีที่ ${i + 1}`);
        input.oninput = () => apply(() => {
          for (const r of s.refs) {
            // a colour that was see-through stays as see-through (only the colour changes)
            const was = new fabric.Color(String(r.obj[r.prop] ?? '#000'));
            const now = new fabric.Color(input.value);
            if (was.getAlpha() < 1) now.setAlpha(was.getAlpha());
            r.obj.set(r.prop, was.getAlpha() < 1 ? now.toRgba() : input.value);
            r.obj.dirty = true;
          }
          s.colour = input.value;
          o.dirty = true;
        });
        box.append(input);
      });
    } else {
      box.querySelectorAll('input').forEach((input, i) => { if (document.activeElement !== input && slots[i]) input.value = slots[i].colour; });
    }
    $('pGfxNote').textContent = slots.length > MAX_SLOTS ? `กราฟิกนี้มี ${slots.length} สี แสดงให้เปลี่ยน ${MAX_SLOTS} สีแรก` : '';
  }

  function syncPanel(o: fabric.Object) {
    $in('pOp').value = String(Math.round((o.opacity ?? 1) * 100)); $('pOpV').textContent = $in('pOp').value + '%';
    if (o.type === 'group') { if (!isTable(o)) syncGfxColours(o); return; }
    if (isThaiText(o)) {
      const size = o.fontSize ?? 64;
      $in('pSize').max = String(Math.max(300, Math.round(size * 2))); $in('pSize').value = String(size); $('pSizeV').textContent = String(Math.round(size));
      $in('pTextColor').value = toHex(textInk(o));
      syncFxPanel(o);
      $in('pSp').value = String(o.charSpacing ?? 0); $('pSpV').textContent = String(o.charSpacing ?? 0);
      $in('pLh').value = String(o.lineHeight ?? 1.35); $('pLhV').textContent = (o.lineHeight ?? 1.35).toFixed(2);
      setPressed('pAlign', b => b.dataset.v === o.textAlign);
    } else {
      const isLine = o.type === 'line' || isPen(o);
      if (o.type === 'line') {
        const sw = o.strokeWidth ?? 1;
        setPressed('pDash', b => b.dataset.v === dashStyleOf(o.strokeDashArray, sw));
        setPressed('pArrow', b => b.dataset.v === arrowEndsOf(o as never));
        $in('pRoundCap').checked = o.strokeLineCap === 'round';
      }
      $('pFillLbl').textContent = isLine ? 'สีเส้น' : 'สีพื้น';
      $in('pFill').value = toHex(isLine ? o.stroke : o.fill);
      $in('pStroke').value = toHex(o.stroke);
      // a pen stroke can be wider than the usual 60 (the highlighter): the slider reaches it, never shrinks it on touch
      $in('pSw').max = String(Math.max(60, Math.ceil((o.strokeWidth ?? 0) * 1.5)));
      $in('pSw').value = String(o.strokeWidth ?? 0); $('pSwV').textContent = String(Math.round(o.strokeWidth ?? 0));
      if (o.type === 'rect') {
        const r = o as fabric.Rect;
        $in('pRad').max = String(Math.round(Math.min(r.width ?? 0, r.height ?? 0) / 2));
        $in('pRad').value = String(r.rx ?? 0); $('pRadV').textContent = String(Math.round(r.rx ?? 0));
      }
    }
  }

  function showTool(tool: string | null) {
    openTool = tool;
    const panel = $('toolPanel');
    panel.hidden = !tool;
    panel.querySelectorAll<HTMLElement>('.trow').forEach(r => { r.hidden = !tool || !(r.dataset.tool ?? '').split(' ').includes(tool); });
  }

  function label(o: fabric.Object | null) {
    const act = canvas.getActiveObject();
    if (!act) return '';
    if (act.type === 'activeSelection') return `เลือกไว้ ${(act as fabric.ActiveSelection).size()} ชิ้น · ทำซ้ำ ล็อก หรือลบได้จากแถบเหนือวัตถุ`;
    if (isThaiText(o)) return 'ข้อความ · แตะสองครั้งเพื่อแก้คำ';
    if (slotOf(o)) return 'ช่องใส่รูป · กด "ใส่รูป" เพื่อเลือกรูปของคุณ';
    if (o?.type === 'path' && (o as { shapeId?: string }).shapeId) return 'รูปทรง · แตะสองครั้งเพื่อพิมพ์ข้อความ';
    if (isTable(o)) return 'ตาราง · แตะสองครั้งหรือกด "แก้ตาราง" เพื่อแก้ข้อความในช่อง';
    return kindName(o) ?? 'วัตถุ';
  }

  /** Rebuild the chips for the current selection (keeping the row's scroll position). */
  function sync() {
    const o = single();
    const has = !!canvas.getActiveObject();
    document.querySelector('.app')!.classList.toggle('has-selection', has);
    // not `hidden`: on a tablet the bar keeps its row while nothing is chosen, so the page never moves under the finger (BUG-38)
    $('ctxbar').classList.toggle('idle', !has);
    $('ctxLabel').textContent = label(o);
    const row = $('ctxChips');
    const keep = row.scrollLeft;
    row.textContent = '';
    photoSheet.sync();   // the picture sheet follows the selection (closes when it is not a picture)
    if (!o) { showTool(null); return; }
    const chips = chipsFor(o);
    if (openTool && !chips.some(c => c.tool === openTool)) openTool = null;
    for (const c of chips) {
      const b = document.createElement('button');
      b.className = 'tchip';
      b.setAttribute('aria-label', c.label);
      b.setAttribute('aria-pressed', String(c.tool ? c.tool === openTool : !!c.pressed));
      if (c.off) b.setAttribute('aria-disabled', 'true');
      b.innerHTML = c.html;
      if (c.hold) { b.classList.add('peekhold'); c.hold(b); row.appendChild(b); continue; }
      b.onclick = () => {
        if (c.off) { toast(c.off); return; }
        if (c.act) { c.act(); return; }
        showTool(openTool === c.tool ? null : c.tool!);
        sync();
      };
      row.appendChild(b);
    }
    row.scrollLeft = keep;
    if (isOpen('shFonts')) fontSheet.sync();   // another text chosen while the font sheet is open
    syncPanel(o);
    showTool(openTool);
  }

  /** a new selection starts with the tool panel closed */
  function onSelection() { openTool = null; sync(); }

  $('ctxDone').onclick = () => { canvas.discardActiveObject(); canvas.requestRenderAll(); };

  const num = (e: Event) => +(e.target as HTMLInputElement).value;
  const val = (e: Event) => (e.target as HTMLInputElement).value;
  $in('pSize').oninput = e => apply(o => o.set('fontSize' as keyof fabric.Object, num(e)));
  // the text's own colour: an effect drawn from it (ตัวกลวง, นีออน) follows (src/editor/textFxDraw.ts)
  $in('pTextColor').oninput = e => apply(o => setTextInk(o, val(e)));

  /*
   * สลับสี (ค): each tap the next colour set (src/editor/textFx.ts PALETTES), one undo step each; the set reached is
   * remembered on the text while the app is open (not saved — the colours themselves are)
   */
  function shuffleColours() {
    const o = single(); if (!o || !isThaiText(o)) return;
    const t = o as fabric.Object & { __palette?: number };
    const i = (t.__palette ?? -1) + 1;
    apply(x => {
      const look = paletteLook(cleanFx((x as { textFx?: unknown }).textFx), i);
      if (look.fx) setTextFx(x, look.fx); else setTextInk(x, look.ink);
    });
    t.__palette = i;
    toast(`ชุดสีที่ ${(i % PALETTES.length) + 1} จาก ${PALETTES.length} · แตะอีกครั้งเพื่อเปลี่ยน เลิกทำได้`);
  }

  /* ---------- เอฟเฟกต์ข้อความ (src/editor/textFx.ts) ---------- */
  const fxOf = (o: fabric.Object): TextFx | null => cleanFx((o as { textFx?: unknown }).textFx);
  /** the sliders are made again only when another text or another effect is chosen — never under the finger */
  let fxBuiltFor: { o: fabric.Object; kind: FxKind | '' } | null = null;
  function syncFxPanel(o: fabric.Object) {
    const fx = fxOf(o), kind = fx?.kind ?? '';
    setPressed('pFxKinds', b => b.dataset.v === kind);
    const box = $('pFxParams');
    if (fxBuiltFor?.o !== o || fxBuiltFor.kind !== kind) {
      fxBuiltFor = { o, kind };
      box.textContent = '';
      if (fx) {
        for (const p of FX_PARAMS[fx.kind]) {
          const row = document.createElement('label'); row.className = 'tfxrow';
          const name = document.createElement('span'); name.className = 'lbl'; name.textContent = p.th;
          const v = document.createElement('span'); v.dataset.v = String(p.key); name.append(v);
          const r = document.createElement('input'); r.type = 'range'; r.min = String(p.min); r.max = String(p.max); r.step = '1';
          r.dataset.k = String(p.key); r.setAttribute('aria-label', p.th);
          r.oninput = () => apply(x => { const now = fxOf(x); if (now) setTextFx(x, { ...now, [p.key]: Number(r.value) }); });
          row.append(name, r); box.append(row);
        }
        if (FX_HAS_COLOR[fx.kind]) {
          const row = document.createElement('label'); row.className = 'tfxrow';
          const name = document.createElement('span'); name.className = 'lbl'; name.textContent = fx.kind === 'bg' ? 'สีพื้นหลัง' : fx.kind === 'outline' ? 'สีขอบ' : 'สีเงา';
          const c = document.createElement('input'); c.type = 'color'; c.dataset.k = 'color';
          c.oninput = () => apply(x => { const now = fxOf(x); if (now) setTextFx(x, { ...now, color: c.value }); });
          row.append(name, c); box.append(row);
        }
      }
    }
    // values only (the inputs stay the same elements); one the finger is on is left alone
    box.querySelectorAll<HTMLInputElement>('input').forEach(i => {
      if (!fx || document.activeElement === i) return;
      const v = (fx as unknown as Record<string, unknown>)[i.dataset.k!];
      if (v !== undefined) i.value = String(v);
    });
    box.querySelectorAll<HTMLElement>('span[data-v]').forEach(s => { s.textContent = fx ? String((fx as unknown as Record<string, unknown>)[s.dataset.v!] ?? '') : ''; });
    $('pFxNote').textContent = !fx ? 'เลือกเอฟเฟกต์ได้ทีละแบบ'
      : ['shadow', 'lift', 'neon', 'echo'].includes(fx.kind) ? 'PDF โรงพิมพ์: กล่องนี้จะเป็นภาพ 300 DPI (เงา/เรืองแสงทำเป็นเส้นไม่ได้) งานจอใช้ได้ปกติ'
      : 'PDF โรงพิมพ์: ยังเป็นเส้นคมทุกขนาด';
  }
  $('pFxKinds').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    const kind = b.dataset.v as FxKind | '';
    apply(o => {
      if (!isThaiText(o)) return;
      const now = fxOf(o);
      if (!kind) { setTextFx(o, null); return; }
      if (now?.kind === kind) return;
      setTextFx(o, newFx(kind, textInk(o)));
    });
  };
  $in('pSp').oninput = e => apply(o => o.set('charSpacing' as keyof fabric.Object, num(e)));
  $in('pLh').oninput = e => apply(o => o.set('lineHeight' as keyof fabric.Object, num(e)));
  $('pAlign').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    apply(x => x.set('textAlign' as keyof fabric.Object, b.dataset.v));
  };
  // a line's and a pen stroke's colour is its stroke; a highlighter keeps its see-through look
  $in('pFill').oninput = e => apply(o => o.set(o.type === 'line' || isPen(o) ? 'stroke' : 'fill',
    (o as { pen?: string }).pen === 'highlighter' ? withAlpha(val(e), PENS.highlighter.alpha) : val(e)));
  $in('pStroke').oninput = e => apply(o => o.set('stroke', val(e)));
  $in('pSw').oninput = e => apply(o => {
    // a dashed line keeps its pattern in step with its width: the style is read at the OLD width (BUG-52)
    const style = o.type === 'line' ? dashStyleOf(o.strokeDashArray, o.strokeWidth ?? 1) : 'solid';
    o.set('strokeWidth', num(e));
    if (style !== 'solid') o.set('strokeDashArray', dashArray(style, num(e)) ?? undefined);
  });
  /* รูปแบบเส้น */
  $('pDash').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    const style = b.dataset.v as DashStyle;
    apply(o => {
      o.set('strokeDashArray', dashArray(style, o.strokeWidth ?? 1) ?? undefined);
      if (style === 'dots') o.set('strokeLineCap', 'round');   // dots are a round cap on a zero-length dash
    });
  };
  $in('pRoundCap').onchange = e => {
    const box = e.target as HTMLInputElement, o = single();
    // dots are round ends on dashes of no length: square ends would draw nothing
    if (!box.checked && o && dashStyleOf(o.strokeDashArray, o.strokeWidth ?? 1) === 'dots') {
      box.checked = true; toast('เส้นแบบจุดต้องมีปลายมน (ไม่งั้นจะไม่เห็นเส้น) เปลี่ยนเป็นเส้นทึบหรือเส้นประก่อน', true); return;
    }
    apply(x => x.set('strokeLineCap', box.checked ? 'round' : 'butt'));
  };
  $('pArrow').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b) return;
    const v = b.dataset.v as ArrowEnds;
    apply(o => o.set({ arrowStart: v === 'start' || v === 'both', arrowEnd: v === 'end' || v === 'both' } as Partial<fabric.Object>));
  };
  $in('pRad').oninput = e => apply(o => { const v = num(e); (o as fabric.Rect).set({ rx: v, ry: v }); });
  $in('pOp').oninput = e => apply(o => o.set('opacity', num(e) / 100));

  /** the object menu's "เป็นพื้นหลัง" (the chosen picture) */
  return { sync, onSelection, asBackground: () => fitPicture('background') };
}
